/**
 * Job Management API Contract
 *
 * ts-rest contract definition for job category, task type, and category-task
 * assignment endpoints used by the works-lite settings wizard.
 *
 * Base path: /works-lite/settings  (mounted in apps/backend/src/routes/index.ts)
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Job Category schemas
// ---------------------------------------------------------------------------

export const jobCategoryItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  code: z.string().nullable(),
  type: z.string().nullable(),
  isSystem: z.boolean().nullable(),
  importName: z.string().nullable(),
  isRuntimeTask: z.boolean().nullable(),
  canAbort: z.boolean().nullable(),
  time: z.number().nullable(),
  jobAuditFormId: z.number().nullable(),
  taskCount: z.number(),
});

export const jobCategoryListResponseSchema = z.object({
  data: z.array(jobCategoryItemSchema),
  total: z.number(),
});

export const createJobCategoryBodySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().optional(),
  type: z.string().optional(),
  isSystem: z.boolean().optional(),
  importName: z.string().optional(),
  isRuntimeTask: z.boolean().optional(),
  canAbort: z.boolean().optional(),
  time: z.coerce.number().optional(),
  jobAuditFormId: z.coerce.number().optional(),
});

export const updateJobCategoryBodySchema = createJobCategoryBodySchema.partial();

// ---------------------------------------------------------------------------
// Task Type schemas
// ---------------------------------------------------------------------------

export const taskTypeItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  isSystem: z.boolean().nullable(),
  formGroup: z.string().nullable(),
  formAssignment: z.string().nullable(),
  canRepeat: z.boolean().nullable(),
  canCopy: z.boolean().nullable(),
  compliantAction: z.boolean().nullable(),
  compliantNonCompliant: z.string().nullable(),
  frequency: z.string().nullable(),
  level: z.string().nullable(),
  requirement: z.string().nullable(),
});

export const taskTypesResponseSchema = z.object({
  data: z.array(taskTypeItemSchema),
  total: z.number(),
});

export const createTaskTypeBodySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  isSystem: z.boolean().optional(),
  formGroup: z.string().optional(),
  formAssignment: z.string().optional(),
  canRepeat: z.boolean().optional(),
  canCopy: z.boolean().optional(),
  compliantAction: z.boolean().optional(),
  compliantNonCompliant: z.string().optional(),
  frequency: z.string().optional(),
  level: z.string().optional(),
  requirement: z.string().optional(),
});

export const updateTaskTypeBodySchema = createTaskTypeBodySchema.partial();

// ---------------------------------------------------------------------------
// Category Task (assignment) schemas
// ---------------------------------------------------------------------------

export const categoryTaskItemSchema = z.object({
  id: z.number(),
  jobCategoryId: z.number().nullable(),
  taskTypeId: z.number().nullable(),
  taskTypeName: z.string().nullable(),
  orderNumber: z.string().nullable(),
  type: z.string().nullable(),
  // Mandatory tasks must be completed before the job can be finished on mobile.
  isMandatory: z.boolean(),
});

export const categoryTasksResponseSchema = z.object({
  data: z.array(categoryTaskItemSchema),
});

export const assignTaskBodySchema = z.object({
  jobCategoryId: z.coerce.number(),
  taskTypeId: z.coerce.number(),
  orderNumber: z.string().optional(),
  type: z.string().optional(),
  // Omitted on assign → mandatory. Planners relax individual tasks afterwards.
  isMandatory: z.boolean().optional(),
});

export const updateCategoryTaskBodySchema = z.object({
  isMandatory: z.boolean(),
});

export const reorderTasksBodySchema = z.object({
  categoryId: z.coerce.number(),
  taskIds: z.array(z.coerce.number()),
});

// ---------------------------------------------------------------------------
// Task Form schemas (task_form linking table)
// ---------------------------------------------------------------------------

export const taskFormItemSchema = z.object({
  id: z.number(),
  taskTypeId: z.number(),
  formId: z.number(),
  formName: z.string().nullable(),
  sequence: z.string().nullable(),
});

export const taskFormsResponseSchema = z.object({
  data: z.array(taskFormItemSchema),
});

/**
 * A task type is limited to a single attached form for now. The `task_form`
 * table and this array shape are retained (rather than collapsing to a single
 * `formId` column) so multi-form support can be re-enabled by lifting `.max(1)`
 * without another schema/API migration.
 */
export const MAX_FORMS_PER_TASK_TYPE = 1;

export const setTaskFormsBodySchema = z.object({
  taskTypeId: z.coerce.number(),
  forms: z
    .array(
      z.object({
        formId: z.coerce.number(),
        sequence: z.coerce.number(),
      }),
    )
    .max(MAX_FORMS_PER_TASK_TYPE, {
      message: 'A task type can have at most one attached form',
    }),
});

export type TaskFormItem = z.infer<typeof taskFormItemSchema>;
export type TaskFormsResponse = z.infer<typeof taskFormsResponseSchema>;
export type SetTaskFormsBody = z.infer<typeof setTaskFormsBodySchema>;

// ---------------------------------------------------------------------------
// Shared error schema
// ---------------------------------------------------------------------------

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

const successResponseSchema = z.object({
  message: z.string(),
});

// ---------------------------------------------------------------------------
// TypeScript types
// ---------------------------------------------------------------------------

export type JobCategoryItem = z.infer<typeof jobCategoryItemSchema>;
export type JobCategoriesListResponse = z.infer<typeof jobCategoryListResponseSchema>;
export type CreateJobCategoryBody = z.infer<typeof createJobCategoryBodySchema>;
export type UpdateJobCategoryBody = z.infer<typeof updateJobCategoryBodySchema>;

export type TaskTypeItem = z.infer<typeof taskTypeItemSchema>;
export type TaskTypesListResponse = z.infer<typeof taskTypesResponseSchema>;
export type CreateTaskTypeBody = z.infer<typeof createTaskTypeBodySchema>;
export type UpdateTaskTypeBody = z.infer<typeof updateTaskTypeBodySchema>;

export type CategoryTaskItem = z.infer<typeof categoryTaskItemSchema>;
export type CategoryTasksListResponse = z.infer<typeof categoryTasksResponseSchema>;
export type AssignTaskBody = z.infer<typeof assignTaskBodySchema>;
export type UpdateCategoryTaskBody = z.infer<typeof updateCategoryTaskBodySchema>;
export type ReorderTasksBody = z.infer<typeof reorderTasksBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const jobManagementContract = c.router({
  // ---- Job Categories ----

  getJobCategories: {
    method: 'GET',
    path: '/job-categories',
    responses: {
      200: jobCategoryListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List all active job categories with task count',
  },

  createJobCategory: {
    method: 'POST',
    path: '/job-categories',
    body: createJobCategoryBodySchema,
    responses: {
      201: jobCategoryItemSchema,
      400: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new job category',
  },

  updateJobCategory: {
    method: 'PUT',
    path: '/job-categories/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateJobCategoryBodySchema,
    responses: {
      200: jobCategoryItemSchema,
      404: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update an existing job category',
  },

  deleteJobCategory: {
    method: 'DELETE',
    path: '/job-categories/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Soft delete a job category',
  },

  // ---- Task Types ----

  getTaskTypes: {
    method: 'GET',
    path: '/task-types',
    responses: {
      200: taskTypesResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List all active task types',
  },

  createTaskType: {
    method: 'POST',
    path: '/task-types',
    body: createTaskTypeBodySchema,
    responses: {
      201: taskTypeItemSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new task type',
  },

  updateTaskType: {
    method: 'PUT',
    path: '/task-types/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateTaskTypeBodySchema,
    responses: {
      200: taskTypeItemSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update an existing task type',
  },

  deleteTaskType: {
    method: 'DELETE',
    path: '/task-types/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Soft delete a task type',
  },

  // ---- Category Tasks ----

  getCategoryTasks: {
    method: 'GET',
    path: '/category-tasks/:categoryId',
    pathParams: z.object({ categoryId: z.coerce.number() }),
    responses: {
      200: categoryTasksResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get tasks assigned to a category',
  },

  assignTask: {
    method: 'POST',
    path: '/category-tasks',
    body: assignTaskBodySchema,
    responses: {
      201: categoryTaskItemSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Assign a task type to a job category',
  },

  removeTaskAssignment: {
    method: 'DELETE',
    path: '/category-tasks/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Remove a task assignment from a category',
  },

  updateCategoryTask: {
    method: 'PATCH',
    path: '/category-tasks/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateCategoryTaskBodySchema,
    responses: {
      200: categoryTaskItemSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Toggle whether a category task assignment is mandatory or optional',
  },

  reorderTasks: {
    method: 'PUT',
    path: '/category-tasks/reorder',
    body: reorderTasksBodySchema,
    responses: {
      200: successResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Reorder tasks within a category',
  },

  // ---- Task Forms ----

  getTaskForms: {
    method: 'GET',
    path: '/task-forms/:taskTypeId',
    pathParams: z.object({ taskTypeId: z.coerce.number() }),
    responses: {
      200: taskFormsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get forms assigned to a task type, ordered by sequence',
  },

  setTaskForms: {
    method: 'PUT',
    path: '/task-forms',
    body: setTaskFormsBodySchema,
    responses: {
      200: taskFormsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Set the forms for a task type (replaces all existing assignments)',
  },
});
